import { HTMLAttributes } from "react";
import {createPortal} from "react-dom";
import { Alert } from "react-bootstrap";

import DOMPurify from "dompurify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {faCheck, faExclamationTriangle, faInfoCircle} from "@fortawesome/free-solid-svg-icons";

import "./notification.scss";

export interface NotificationProps extends HTMLAttributes<HTMLDivElement> {
  show: boolean;
  type:
    | "dark"
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "warning"
    | "info"
    | "light";
  category?:
      | "user"
      | "app"
      | "action"
  autoClose?: boolean;
  onClose?: () => void;
  /*
   * true makes the element closable
   */
  dismissible?: boolean;
  /**
   * override default icon
   */
  icon?: IconProp;
  asChild?: boolean;
  target?: HTMLElement|null;
}
//</editor-fold>

function Notification(props: NotificationProps) {
  //<editor-fold desc="Local variables">
  let {
    id,
    show,
    type,
    category,
    onClose,
    children,
    autoClose,
    dismissible,
    dangerouslySetInnerHTML,
    icon,
    className,
    asChild,
      target,
    ...other
  } = props;
  const icons = {
    dark: faInfoCircle,
    primary: faInfoCircle,
    secondary: faInfoCircle,
    success: faCheck,
    danger: faExclamationTriangle,
    warning: faExclamationTriangle,
    info: faInfoCircle,
    light: faInfoCircle,
  };
  let nclassName = (className ? className + " " : "") + "notification " + (category || "");
  if (autoClose) {
    nclassName =
      (className ? className + " " : "") + "notification auto-close shadow " + (category || "");
  }
  //</editor-fold>
  const cont = (
    <Alert
      id={id}
      data-test-feedback={type}
      variant={type}
      show={show}
      onClose={onClose}
      className={nclassName}
      dismissible={dismissible !== undefined ? dismissible : false}
      {...other}
    >
      <div className={"notification-content"}>
        {children && <div className="notification-copy">{children}</div>}
        {/* TODO: The typing has changed from orgadmin and correct typing is still lost */}
        {dangerouslySetInnerHTML && (
          <div
            className="notification-copy"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(dangerouslySetInnerHTML.__html as string|Node),
            }}
          ></div>
        )}
        <div className={"notification-icon icon"}>
          <FontAwesomeIcon icon={(icon || icons[type]) as IconProp} />
        </div>
      </div>
    </Alert>
  );
  // allows use of same concept from localize,  which currently sitst outside this provider
  const fallbackTarget = target || document.getElementById('notifications');
  if (asChild || !fallbackTarget) {
    return cont;
  } else {
    // display contents in portal to allow stacking notifications from different origins
    return createPortal(cont, fallbackTarget);
  }
}
export default Notification;
